import { Component, OnInit, EventEmitter, Output, Input, AfterViewInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
// import { DateRangeComponent, DateRangeOptions } from '../date-range/date-range.component'; // No longer needed
import { ToastNotificationComponent, ToastType } from '../toast-notification/toast-notification.component';
import { CampaignService, Campaign } from '../../services/campaign.service';
import { MonthRangeComponent } from '../month-range/month-range.component';
// Define an interface for the campaign selection event
export interface CampaignSelection {
  id: string;
  name: string;
  fromDate: string;
  toDate: string;
}

// Define an interface for the performance request
export interface CampaignPerformanceRequest {
  campaignCode: string;
  campaignName: string | null;
  fromDate: string;
  toDate: string;
}

@Component({
  selector: 'app-campaign-filter',
  standalone: true,
  imports: [CommonModule, FormsModule, ToastNotificationComponent, MonthRangeComponent],
  templateUrl: './campaign-filter.component.html',
  styleUrls: ['./campaign-filter.component.scss']
})
export class CampaignFilterComponent implements OnInit, AfterViewInit {
  @ViewChild('monthRange') private monthRange?: MonthRangeComponent;

  // Inputs
  @Input() showAllCampaignsOption: boolean = false; // New input to control "All Campaigns" option
  @Input() CampaignType: number = 1; // New input for campaign type, default 1
  
  // Outputs
  @Output() campaignSelected = new EventEmitter<string>();
  @Output() performanceRequested = new EventEmitter<CampaignPerformanceRequest>();
  
  // Data
  campaigns: Campaign[] = [];
  selectedCampaignCode: string | null = null;
  selectedCampaignName: string | null = null;
  fromDate: string;
  toDate: string;
  dateRangeInvalid = false;
  
  // UI state
  campaignsLoading = false;
  campaignSearch = '';
  
  // Toast notification
  toastVisible = false;
  toastType: ToastType = 'error';
  toastTitle = '';
  toastMessage = '';
  
  constructor(private campaignService: CampaignService) {
    const range = this.getDefaultDateRange();
    this.fromDate = range.fromDate;
    this.toDate = range.toDate;
  }
  
  ngOnInit(): void {
    this.loadCampaigns();
  }
  
  // Add AfterViewInit to fix dropdown positioning issues
  ngAfterViewInit() {
    // Basic setup for dropdowns
    document.querySelectorAll('.enhanced-dropdown').forEach(dropdown => {
      dropdown.addEventListener('shown.bs.dropdown', () => {
        const dropdownMenu = dropdown.querySelector('.dropdown-menu');
        if (dropdownMenu) {
          // Force extreme z-index
          (dropdownMenu as HTMLElement).style.zIndex = '99999';
          
          // Move to document body to escape stacking contexts
          document.body.appendChild(dropdownMenu);
          
          // Position relative to the dropdown toggle
          const toggle = dropdown.querySelector('.dropdown-toggle');
          if (toggle) {
            const rect = toggle.getBoundingClientRect();
            (dropdownMenu as HTMLElement).style.position = 'fixed';
            (dropdownMenu as HTMLElement).style.top = `${rect.bottom + window.scrollY}px`;
            (dropdownMenu as HTMLElement).style.left = `${rect.left + window.scrollX}px`;
            (dropdownMenu as HTMLElement).style.width = `${rect.width}px`;
            
            // Add max-height constraint
            (dropdownMenu as HTMLElement).style.maxHeight = '400px';
            (dropdownMenu as HTMLElement).style.overflowY = 'auto';
            
            // Store the original parent for later restoration
            dropdownMenu.setAttribute('data-parent-dropdown', dropdown.id || 'dropdown');
          }
        }
      });
      
      // Return the dropdown menu to its original parent when hidden
      dropdown.addEventListener('hidden.bs.dropdown', () => {
        const dropdownMenu = document.querySelector('.dropdown-menu[data-parent-dropdown]');
        if (dropdownMenu && dropdownMenu.getAttribute('data-parent-dropdown') === dropdown.id) {
          dropdown.appendChild(dropdownMenu);
        }
      });
    });
    
    // Add global event listener to ensure dropdowns stay on top
    window.addEventListener('click', () => {
      setTimeout(() => {
        document.querySelectorAll('.dropdown-menu.show').forEach(menu => {
          (menu as HTMLElement).style.zIndex = '99999';
        });
      }, 0);
    });
  }
  
  // Date range handlers
  onDateRangeChanged(dateRange: { fromDate: string, toDate: string }): void {
    this.fromDate = dateRange.fromDate;
    this.toDate = dateRange.toDate;
  }
  
  onDateRangeValidationChanged(isValid: boolean): void {
    this.dateRangeInvalid = !isValid;
  }
  
  // Campaign loading
  loadCampaigns(options?: { emitPerformanceAfterLoad?: boolean }): void {
    const emitPerformanceAfterLoad = options?.emitPerformanceAfterLoad === true;

    this.campaignsLoading = true;
    this.campaigns = [];
    this.selectedCampaignCode = null;
    
    // Format dates for API (YYYYMM format)
    const fromDateFormatted = this.fromDate.replace('-', '').substring(0, 6);
    const toDateFormatted = this.toDate.replace('-', '').substring(0, 6);
    
    this.campaignService.getCampaigns(fromDateFormatted, toDateFormatted, this.CampaignType)
      .subscribe({
        next: (campaigns) => {
          this.campaigns = campaigns;
          this.campaignsLoading = false;
          
          // Set default selection based on showAllCampaignsOption
          if (this.showAllCampaignsOption) {
            this.selectedCampaignCode = 'All Campaigns';
            this.selectedCampaignName = 'All Campaigns';
          } else if (campaigns.length > 0) {
            // If "All Campaigns" is not shown, select the first campaign
            this.selectedCampaignCode = campaigns[0].id;
            this.selectedCampaignName = campaigns[0].name;
          }
          
          if (campaigns.length === 0) {
            this.showToast('info', 'No Campaigns', 'No campaigns found for the selected date range.');
          } else if (this.CampaignType === 1 && this.selectedCampaignCode) {
            // Dot Digital: emit initial performance request so data loads on page load
            this.getCampaignPerformance();
          } else if (emitPerformanceAfterLoad && this.selectedCampaignCode) {
            // HUM and other types: parent listens to performanceRequested (e.g. after Reset)
            this.getCampaignPerformance();
          }
        },
        error: (error) => {
          this.campaignsLoading = false;
          this.showToast('error', 'Error', 'Failed to load campaigns. Please try again.');
          console.error('Error loading campaigns:', error);
        }
      });
  }
  
  // Campaign selection
  selectCampaign(campaignId: string, event: Event): void {
    // Set the selected campaign
    this.selectedCampaignCode = campaignId;
    this.selectedCampaignName = this.campaigns.find(c => c.id === campaignId)?.name || null;
    this.campaignSelected.emit(campaignId);
    
    // Prevent default link behavior
    event.preventDefault();
    
    // Close the dropdown using Bootstrap's API if available
    const dropdownToggle = (event.target as HTMLElement).closest('.dropdown')?.querySelector('[data-bs-toggle="dropdown"]');
    if (dropdownToggle) {
      // Try to use Bootstrap's API
      try {
        // @ts-ignore - Bootstrap might be available globally
        const bsDropdown = bootstrap.Dropdown.getInstance(dropdownToggle);
        if (bsDropdown) {
          bsDropdown.hide();
          return;
        }
      } catch (e) {
        // Fallback if Bootstrap API is not available
        console.warn('Bootstrap API not available, using fallback');
      }
    }
    
    // Fallback - simulate a click outside
    document.body.click();
  }
  
  getSelectedCampaignName(): string {
    if (!this.selectedCampaignCode) return 'Select Campaign';
    if (this.selectedCampaignCode === 'All Campaigns' && this.showAllCampaignsOption) return 'All Campaigns';
    
    const campaign = this.campaigns.find(c => c.id === this.selectedCampaignCode);
    return campaign ? campaign.name : 'Select Campaign';
  }
  
  // Performance data
  getCampaignPerformance(): void {
    if (!this.selectedCampaignCode) return;

    const performanceRequest: CampaignPerformanceRequest = {
      campaignCode: this.selectedCampaignCode,
      campaignName : this.selectedCampaignName,
      fromDate: this.fromDate.replace('-', '').substring(0, 6), // Convert to YYYYMM format
      toDate: this.toDate.replace('-', '').substring(0, 6) // Convert to YYYYMM format
    };

    this.performanceRequested.emit(performanceRequest);
  }
  
  // Campaign search
  get filteredCampaigns(): Campaign[] {
    if (!this.campaignSearch) return this.campaigns;
    
    const search = this.campaignSearch.toLowerCase();
    return this.campaigns.filter(campaign => 
      campaign.name.toLowerCase().includes(search)
    );
  }
  
  clearCampaignSearch(): void {
    this.campaignSearch = '';
  }
  
  // Toast notifications
  showToast(type: ToastType, title: string, message: string): void {
    this.toastType = type;
    this.toastTitle = title;
    this.toastMessage = message;
    this.toastVisible = true;
  }
  
  onToastClosed(): void {
    this.toastVisible = false;
  }

  /**
   * Restore default date range (YTD — same as MonthRangeComponent first load / Reset), clear campaign search,
   * reload campaigns and emit performance so parents refresh (Dot Digital on any load; HUM/other via flag).
   */
  resetCampaignFilters(): void {
    this.campaignSearch = '';
    this.monthRange?.resetToDefaultRange();
    this.loadCampaigns({ emitPerformanceAfterLoad: true });
  }

  /** Year-to-date (Jan 1 → current month), matching MonthRangeComponent.setRangeYearToDate(). */
  private getDefaultDateRange(): { fromDate: string; toDate: string } {
    const today = new Date();
    const to = new Date(today.getFullYear(), today.getMonth(), 1);
    const from = new Date(today.getFullYear(), 0, 1);
    const fromDate = `${from.getFullYear()}-${(from.getMonth() + 1).toString().padStart(2, '0')}`;
    const toDate = `${to.getFullYear()}-${(to.getMonth() + 1).toString().padStart(2, '0')}`;
    return { fromDate, toDate };
  }
} 